import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../client';
import { showSuccess, showError } from '../../lib/alerts';

function unwrapApiResponse(data: any): any {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data;
  return data;
}

export const useAiAgents = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-agents'],
    queryFn: () => client.get('/api/admin/ai/agents').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 5,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/agents', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-agents'] }); showSuccess('Agente criado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar agente.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/agents/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-agents'] }); showSuccess('Agente atualizado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar agente.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/agents/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-agents'] }); showSuccess('Agente excluído com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir agente.'),
  });

  return { ...list, create, update, remove };
};

export const useAiModels = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-models'],
    queryFn: () => client.get('/api/admin/ai/models').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 10,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/models', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-models'] }); showSuccess('Modelo criado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar modelo.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/models/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-models'] }); showSuccess('Modelo atualizado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar modelo.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/models/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-models'] }); showSuccess('Modelo excluído com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir modelo.'),
  });

  return { ...list, create, update, remove };
};

export const useAiSystemPrompts = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-system-prompts'],
    queryFn: () => client.get('/api/admin/ai/system-prompts').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 10,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/system-prompts', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-system-prompts'] }); showSuccess('Prompt criado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar prompt.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/system-prompts/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-system-prompts'] }); showSuccess('Prompt atualizado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar prompt.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/system-prompts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-system-prompts'] }); showSuccess('Prompt excluído com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir prompt.'),
  });

  return { ...list, create, update, remove };
};

export const useAiProviders = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-providers'],
    queryFn: () => client.get('/api/admin/ai/providers').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 5,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/providers', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-providers'] }); showSuccess('Provedor criado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar provedor.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/providers/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-providers'] }); showSuccess('Provedor atualizado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar provedor.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/providers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-providers'] }); showSuccess('Provedor excluído com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir provedor.'),
  });

  return { ...list, create, update, remove };
};

export const useAiTools = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-tools'],
    queryFn: () => client.get('/api/admin/ai/tools').then(r => r.data).then(unwrapApiResponse),
    staleTime: 0,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/tools', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-tools'] }); showSuccess('Ferramenta criada com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar ferramenta.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/tools/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-tools'] }); showSuccess('Ferramenta atualizada com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar ferramenta.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/tools/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-tools'] }); showSuccess('Ferramenta excluída com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir ferramenta.'),
  });

  return { ...list, create, update, remove };
};

export const useAiContexts = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-contexts'],
    queryFn: () => client.get('/api/admin/ai/contexts').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 5,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/contexts', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-contexts'] }); showSuccess('Contexto criado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar contexto.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/contexts/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-contexts'] }); showSuccess('Contexto atualizado com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar contexto.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/contexts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-contexts'] }); showSuccess('Contexto excluído com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir contexto.'),
  });

  return { ...list, create, update, remove };
};

export const useAiPolicies = () => {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['ai-policies'],
    queryFn: () => client.get('/api/admin/ai/policies').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 60 * 5,
  });

  const create = useMutation({
    mutationFn: (data: any) => client.post('/api/admin/ai/policies', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-policies'] }); showSuccess('Política criada com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao criar política.'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => client.put(`/api/admin/ai/policies/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-policies'] }); showSuccess('Política atualizada com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao atualizar política.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => client.delete(`/api/admin/ai/policies/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-policies'] }); showSuccess('Política excluída com sucesso!'); },
    onError: (err: any) => showError(err?.response?.data?.message || 'Erro ao excluir política.'),
  });

  return { ...list, create, update, remove };
};

export const useAiMetrics = () => {
  return useQuery({
    queryKey: ['ai-metrics'],
    queryFn: () => client.get('/api/admin/ai/metrics').then(r => r.data).then(unwrapApiResponse),
    staleTime: 1000 * 30,
  });
};
